import { restrictionTypeLabel } from "@/features/admin/restrictionLabels";
import type {
  RecommendationItem,
  RestrictionImpact,
  RestrictionOutcomeItem,
} from "@/types/api";
import type { RestrictionSeverity } from "@/types/restrictions";

export interface OpportunityRestrictionNote {
  severity: RestrictionSeverity | "blocked";
  label: string;
  reason: string;
}

function formatSignedDays(label: string, days: number): string {
  const sign = days > 0 ? "+" : "";
  return `${label} ${sign}${days}d`;
}

function formatLegDelta(delta: number): string {
  const abs = Math.abs(delta);
  const noun = abs === 1 ? "leg" : "legs";
  return delta > 0
    ? `${delta} additional transshipment ${noun}`
    : `${abs} fewer transshipment ${noun}`;
}

function formatScheduleImpact(impact: RestrictionImpact): string | null {
  if (impact.type !== "schedule_change") return null;
  const parts: string[] = [];
  if (impact.eta_delta_days != null && impact.eta_delta_days !== 0) {
    parts.push(formatSignedDays("ETA", impact.eta_delta_days));
  }
  if (impact.leg_delta != null && impact.leg_delta !== 0) {
    parts.push(formatLegDelta(impact.leg_delta));
  }
  if (impact.transit_delta_days != null && impact.transit_delta_days !== 0) {
    parts.push(formatSignedDays("Transit", impact.transit_delta_days));
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

function isBlockSeverity(severity: string): boolean {
  return severity === "blocked" || severity === "block";
}

function appendUnique(lines: string[], line: string | null | undefined) {
  if (!line) return;
  const trimmed = line.trim();
  if (!trimmed || lines.includes(trimmed)) return;
  lines.push(trimmed);
}

/** Plain-language schedule deltas for an opportunity card. */
export function collectScheduleChangeLines(rec: RecommendationItem): string[] {
  const lines: string[] = [];
  const sailing = rec.schedule_match;
  const original = rec.original_schedule;

  if (rec.etd_shift_days != null && rec.etd_shift_days !== 0) {
    appendUnique(
      lines,
      `Departure shifts ${rec.etd_shift_days > 0 ? "later" : "earlier"} by ${Math.abs(rec.etd_shift_days)} day${Math.abs(rec.etd_shift_days) === 1 ? "" : "s"}`,
    );
  }

  if (sailing?.etd_diff_days != null && sailing.etd_diff_days !== 0) {
    appendUnique(lines, formatSignedDays("ETD", sailing.etd_diff_days));
  }

  if (
    sailing?.eta_diff_days != null &&
    sailing.eta_diff_days !== 0 &&
    sailing.eta_diff_days !== sailing.etd_diff_days
  ) {
    appendUnique(lines, formatSignedDays("ETA", sailing.eta_diff_days));
  }

  if (sailing?.leg_diff != null && sailing.leg_diff !== 0) {
    appendUnique(lines, formatLegDelta(sailing.leg_diff));
  } else if (
    sailing?.leg_count != null &&
    original?.leg_count != null &&
    sailing.leg_count !== original.leg_count
  ) {
    appendUnique(lines, formatLegDelta(sailing.leg_count - original.leg_count));
  }

  if (
    sailing?.transit_time != null &&
    original?.transit_time != null &&
    sailing.transit_time !== original.transit_time
  ) {
    appendUnique(
      lines,
      formatSignedDays("Transit", sailing.transit_time - original.transit_time),
    );
  }

  if (
    sailing?.direct != null &&
    original?.direct != null &&
    sailing.direct !== original.direct
  ) {
    appendUnique(lines, sailing.direct ? "Direct sailing" : "Transshipment required");
  }

  for (const outcome of rec.restriction_snapshot?.outcomes ?? []) {
    appendUnique(lines, formatScheduleImpact(outcome.impact));
  }

  return lines;
}

function mapOutcome(outcome: RestrictionOutcomeItem): OpportunityRestrictionNote | null {
  const reason = outcome.reason?.trim();
  if (!reason) return null;
  return {
    severity: isBlockSeverity(outcome.severity)
      ? "blocked"
      : (outcome.severity as RestrictionSeverity),
    label: restrictionTypeLabel(outcome.rule_type),
    reason,
  };
}

/** Restriction warnings and block reasons attached to an opportunity. */
export function collectOpportunityRestrictionNotes(
  rec: RecommendationItem,
): OpportunityRestrictionNote[] {
  return (rec.restriction_snapshot?.outcomes ?? [])
    .map(mapOutcome)
    .filter((note): note is OpportunityRestrictionNote => note != null);
}

export function partitionOpportunityRestrictionNotes(
  notes: OpportunityRestrictionNote[],
): {
  warnings: OpportunityRestrictionNote[];
  blocks: OpportunityRestrictionNote[];
} {
  return {
    warnings: notes.filter((note) => note.severity !== "blocked"),
    blocks: notes.filter((note) => note.severity === "blocked"),
  };
}
